import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  FileDownload as ExportIcon,
  TrendingUp as TrendUpIcon,
  TrendingDown as TrendDownIcon,
  TrendingFlat as TrendFlatIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { ActionButton, PageShell, DataTable, type DataTableColumn } from '../../components';
import { useAcao } from '../../authorization/useVeredito';
import psychosocialService from '../../services/psychosocialService';
import { PsychosocialDashboardData } from '../../types/psychosocial';

const classificationColors: Record<string, string> = {
  green: '#4caf50',
  yellow: '#ff9800',
  red: '#f44336',
};

type DimensionClassification = PsychosocialDashboardData['classificationByDimension'][number];
type HeatmapDepartment = PsychosocialDashboardData['heatmap']['departments'][number];

const dashboardBreadcrumbs = (id: string | undefined) => [
  { label: 'Psicossocial', path: '/psychosocial' },
  { label: 'Campanha', path: `/psychosocial/campaigns/${id}` },
  { label: 'Dashboard' },
];

const PsychosocialCampaignDashboardPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  // O relatório agregado é o que sobra para quem não alcança a resposta individual.
  const resultadoIndividual = useAcao('psychosocial_response.view_individual', 'Ver Resultados');
  const veResultadoIndividual = !resultadoIndividual.disabled;

  const [dashboard, setDashboard] = useState<PsychosocialDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const data = await psychosocialService.getDashboard(Number(id));
      setDashboard(data);
    } catch {
      enqueueSnackbar('Erro ao carregar dashboard', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, enqueueSnackbar]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleExport = async () => {
    try {
      const blob = await psychosocialService.exportExcel(Number(id));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resultados-psicossocial-${id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar('Erro ao exportar', { variant: 'error' });
    }
  };

  const heatmapColumns = useMemo<DataTableColumn<DimensionClassification>[]>(() => {
    const departments: HeatmapDepartment[] = dashboard?.heatmap.departments ?? [];
    return [
      {
        id: 'dimension',
        label: 'Dimensão',
        primary: true,
        render: (dim) => <Box component="span" sx={{ fontSize: 12 }}>{dim.dimensionName}</Box>,
      },
      ...departments.map((dept) => ({
        id: `dept-${dept.departmentId}`,
        label: dept.departmentName,
        align: 'center' as const,
        render: (dim: DimensionClassification) => {
          const val = dept.dimensions[dim.dimensionKey];
          if (!val) return '-';
          return (
            <Chip
              size="small"
              label={val.avgScore.toFixed(0)}
              sx={{
                bgcolor: classificationColors[val.classification],
                color: 'white',
                fontWeight: 'bold',
                minWidth: 40,
              }}
            />
          );
        },
      })),
    ];
  }, [dashboard]);

  if (loading) {
    return (
      <PageShell
        breadcrumbs={dashboardBreadcrumbs(id)}
        title="Dashboard da Campanha"
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      </PageShell>
    );
  }

  if (!dashboard) return null;

  const { status, classificationByDimension, heatmap, riskRanking, comparisonWithPrevious } = dashboard;

  return (
    <PageShell
      breadcrumbs={dashboardBreadcrumbs(id)}
      title="Dashboard da Campanha"
      subtitle="Visualize os resultados e métricas da campanha"
      actions={
        <>
          <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExport}>
            {veResultadoIndividual ? 'Exportar Excel' : 'Exportar Relatório'}
          </Button>
          <ActionButton
            acao="psychosocial_response.view_individual"
            rotulo="Ver Resultados"
            variant="outlined"
            onClick={() => navigate(`/psychosocial/campaigns/${id}/results`)}
          >
            Ver Resultados
          </ActionButton>
        </>
      }
    >
      {/* Status Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: status.total, color: '#1976d2' },
          { label: 'Respondidos', value: status.completed, color: '#4caf50' },
          { label: 'Pendentes', value: status.pending + status.notified, color: '#ff9800' },
          { label: 'Expirados', value: status.expired, color: '#f44336' },
          { label: 'Taxa', value: `${status.completionRate}%`, color: '#9c27b0' },
        ].map((card) => (
          <Grid size={{ xs: 6, md: 'grow' }} key={card.label}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                <Typography variant="h4" sx={{ color: card.color, fontWeight: 'bold' }}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Classification by Dimension */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Distribuição por Dimensão</Typography>
          {classificationByDimension.map((dim) => (
            <Box key={dim.dimensionKey} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{dim.dimensionName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {dim.green + dim.yellow + dim.red} respostas
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', height: 24, borderRadius: 1, overflow: 'hidden' }}>
                {dim.greenPct > 0 && (
                  <Tooltip title={`Verde: ${dim.greenPct}%`}>
                    <Box sx={{ width: `${dim.greenPct}%`, bgcolor: classificationColors.green, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'white', fontSize: 10 }}>{dim.greenPct > 10 ? `${dim.greenPct}%` : ''}</Typography>
                    </Box>
                  </Tooltip>
                )}
                {dim.yellowPct > 0 && (
                  <Tooltip title={`Amarelo: ${dim.yellowPct}%`}>
                    <Box sx={{ width: `${dim.yellowPct}%`, bgcolor: classificationColors.yellow, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'white', fontSize: 10 }}>{dim.yellowPct > 10 ? `${dim.yellowPct}%` : ''}</Typography>
                    </Box>
                  </Tooltip>
                )}
                {dim.redPct > 0 && (
                  <Tooltip title={`Vermelho: ${dim.redPct}%`}>
                    <Box sx={{ width: `${dim.redPct}%`, bgcolor: classificationColors.red, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'white', fontSize: 10 }}>{dim.redPct > 10 ? `${dim.redPct}%` : ''}</Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: classificationColors.green, borderRadius: '50%' }} />
              <Typography variant="caption">Baixo risco</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: classificationColors.yellow, borderRadius: '50%' }} />
              <Typography variant="caption">Moderado</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: classificationColors.red, borderRadius: '50%' }} />
              <Typography variant="caption">Alto risco</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Heatmap */}
        {heatmap.departments.length > 0 && (
          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Heatmap: Dimensões x Setores</Typography>
                <DataTable
                  columns={heatmapColumns}
                  rows={classificationByDimension}
                  getRowId={(dim) => dim.dimensionKey}
                  emptyState={{
                    title: 'Nenhuma dimensão avaliada nesta campanha',
                    description:
                      'O heatmap é calculado a partir das respostas que os funcionários enviam ao formulário desta campanha.',
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Risk Ranking */}
        {riskRanking.byDepartment.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Top Setores em Risco</Typography>
                {riskRanking.byDepartment.slice(0, 5).map((dept, idx) => (
                  <Box key={dept.departmentId} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" sx={{ minWidth: 20 }}>{idx + 1}.</Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>{dept.departmentName}</Typography>
                    <Chip
                      size="small"
                      label={`${dept.redPct}% vermelho`}
                      color="error"
                      variant="outlined"
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Comparison with previous */}
        {comparisonWithPrevious && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Comparativo com "{comparisonWithPrevious.previousCampaignName}"
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Taxa de adesão: {comparisonWithPrevious.completionRateDelta > 0 ? '+' : ''}{comparisonWithPrevious.completionRateDelta}%
                </Typography>
                {comparisonWithPrevious.dimensionDeltas.slice(0, 8).map((delta) => (
                  <Box key={delta.dimensionKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {delta.trend === 'worsening' && <TrendUpIcon color="error" fontSize="small" />}
                    {delta.trend === 'improving' && <TrendDownIcon color="success" fontSize="small" />}
                    {delta.trend === 'stable' && <TrendFlatIcon color="action" fontSize="small" />}
                    <Typography variant="body2" sx={{ flex: 1 }}>{delta.dimensionName}</Typography>
                    <Typography
                      variant="body2"
                      color={delta.trend === 'worsening' ? 'error' : delta.trend === 'improving' ? 'success.main' : 'text.secondary'}
                    >
                      {delta.delta > 0 ? '+' : ''}{delta.delta}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </PageShell>
  );
};

export default PsychosocialCampaignDashboardPage;
